import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { finalize } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';

import { AuthService } from '../../auth-pages/services/auth.service';
import { UserApiService } from '../services/user-api.service';
import {
  CrmRegisterRequest,
  CrmUpdateUserRequest,
  CrmUserResponse,
  CrmRoleResponse
} from '../models/user.model';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './user-form.component.html'
})
export class UserFormComponent implements OnInit {

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';

  userId: number | null = null;
  isEdit = false;

  businessId!: number;
  currentRoleId!: number;
  isSuperAdmin = false;
  showPassword = false;

  allRoles: CrmRoleResponse[] = [];
  rolesLoading = false;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private api: UserApiService,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    const bId = this.auth.getBusinessId();
    const cu = this.auth.getCurrentUser();

    this.businessId = bId ?? 0;
    this.currentRoleId = (cu as any)?.roleId ?? (cu as any)?.idRol ?? 999;
    this.isSuperAdmin = this.currentRoleId === 1;

    const idParam = this.route.snapshot.paramMap.get('id');

    // `isEdit` DEBE quedar resuelto ANTES de construir el form: la contraseña solo es
    // obligatoria en el alta, y `initForm()` decide sus validadores a partir de esta bandera.
    // Cuando se asignaba después, el control quedaba con `required` en edición sobre un campo
    // que la plantilla ni siquiera renderiza (@if (!isEdit)) → el form nunca llegaba a válido
    // y el botón "Actualizar" quedaba muerto sin explicación visible.
    this.userId = idParam ? Number(idParam) : null;
    this.isEdit = !!idParam;

    this.initForm();

    if (this.isEdit && !this.isSuperAdmin) {
      this.error = 'No tienes permisos para editar este usuario.';
      setTimeout(() => this.router.navigate(['/users']), 1000);
      return;
    }

    this.loadRoles(() => {
      if (this.isEdit) this.loadUser();
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      fullname: ['', [Validators.required, Validators.maxLength(100), this.noOnlyWhitespaceValidator]],
      lastname: ['', [Validators.required, Validators.maxLength(100), this.noOnlyWhitespaceValidator]],
      mail: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      // Los validadores de contraseña NO se declaran aquí: dependen del modo (alta vs edición).
      password: [''],
      idRol: [null as number | null, [Validators.required]],
      isActive: [true],
    });

    this.syncPasswordValidators();
  }

  /**
   * La contraseña solo existe en el alta. En edición el bloque no se renderiza (la plantilla lo
   * envuelve en `@if (!isEdit)`) y `CrmUpdateUserRequest` tampoco la transporta, así que exigirla
   * sería pedir algo que el usuario no puede rellenar ni enviar.
   *
   * <p>Se aplica también fuera de `initForm()` por seguridad: `initForm()` corre en el
   * constructor, cuando `isEdit` todavía es `false`, y se vuelve a llamar en `ngOnInit` con la
   * bandera ya resuelta. El alta conserva exactamente las mismas reglas de antes.</p>
   */
  private syncPasswordValidators(): void {
    const control = this.form.get('password');
    if (!control) return;

    if (this.isEdit) {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
    } else {
      control.setValidators([Validators.required, Validators.minLength(8), this.passwordStrengthValidator]);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private loadRoles(after?: () => void): void {
    this.rolesLoading = true;

    this.api.getAssignableRoles()
      .pipe(finalize(() => (this.rolesLoading = false)))
      .subscribe({
        next: (res) => {
          this.allRoles = res ?? [];

          const current = this.form.get('idRol')?.value;

          if (!current && !this.isEdit) {
            const first = this.allRoles[0]?.id ?? null;
            if (first) this.form.patchValue({ idRol: first }, { emitEvent: false });
          }

          if (after) after();
        },
        error: () => {
          this.allRoles = [];
          if (after) after();
        }
      });
  }

  get selectedRoleName(): string {
    const id = this.form?.value?.idRol;
    if (!id) return '';
    const role = this.allRoles.find(r => r.id === id);
    return role?.name ?? '';
  }

  isValidField(field: string): boolean {
    const c = this.form.get(field);
    return !!(c && c.valid && (c.touched || c.dirty));
  }

  private loadUser(): void {
    if (!this.userId) return;

    this.loading = true;

    this.api.getById(this.userId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (u: CrmUserResponse) => {
          this.form.patchValue({
            fullname: u.fullname ?? '',
            lastname: u.lastname ?? '',
            mail: u.mail ?? '',
            idRol: u.idRol ?? null,
            isActive: u.isActive ?? true
          });
        },
        error: () => {
          this.error = 'No se pudo cargar el usuario.';
        }
      });
  }

  roleDisabled(optionId: number): boolean {
    return optionId < this.currentRoleId;
  }

  save(): void {
    this.normalizeFormValues();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error = 'Revisa los campos antes de continuar.';
      // El botón ya no se deshabilita por `form.invalid`: si el clic no hiciera nada, un campo
      // inválido fuera de la vista dejaría la pantalla muda (era justo lo que pasaba con la
      // contraseña en edición).
      this.toast.warning({ title: 'Faltan datos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    if (this.isEdit && this.userId) {
      const req: CrmUpdateUserRequest = {
        fullname: this.form.value.fullname ?? null,
        lastname: this.form.value.lastname ?? null,
        mail: this.form.value.mail ?? null,
        isActive: this.form.value.isActive ?? null,
        idRol: this.form.value.idRol ?? null,
      };

      this.api.updateAsSuperAdmin(this.userId, req)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: () => {
            this.toast.updated('Usuario');
            this.router.navigate(['/users'])
          },
          error: (err) => {
            this.error = err?.status === 409
              ? 'El correo ya está registrado en esta empresa.'
              : err?.status === 403
                ? 'No puedes asignar un rol con privilegios iguales o superiores al tuyo.'
                : 'No se pudo actualizar el usuario.';
            this.toast.error({ title: 'Error', message: this.error });
          }
        });

      return;
    }

    const req: CrmRegisterRequest = {
      fullname: this.form.value.fullname!,
      lastname: this.form.value.lastname!,
      mail: this.form.value.mail!,
      password: this.form.value.password!,
      businessId: this.businessId,
      idRol: this.form.value.idRol,
    };

    this.api.create(req)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.toast.created('Usuario'); 
          this.router.navigate(['/users'])
        },
        error: (err) => {
          this.error = err?.status === 409
            ? 'El correo ya está registrado.'
            : 'No se pudo crear el usuario.';
            this.toast.error({ title: 'Error', message: this.error });
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/users']);
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!(c && c.invalid && (c.touched || c.dirty));
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c || !c.errors) return '';

    if (c.errors['required']) return 'Este campo es obligatorio';
    if (c.errors['email']) return 'Correo inválido';
    if (c.errors['minlength']) return `Mínimo ${c.errors['minlength'].requiredLength} caracteres`;
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    if (c.errors['whitespace']) return 'No puede estar vacío';
    if (c.errors['missingUpper']) return 'Debe incluir al menos una mayúscula';
    if (c.errors['missingLower']) return 'Debe incluir al menos una minúscula';
    if (c.errors['missingNumber']) return 'Debe incluir al menos un número';
    if (c.errors['missingSpecial']) return 'Debe incluir al menos un carácter especial';

    return 'Campo inválido';
  }

  private normalizeFormValues(): void {
    this.form.patchValue({
      fullname: String(this.form.value.fullname ?? '').trim(),
      lastname: String(this.form.value.lastname ?? '').trim(),
      mail: String(this.form.value.mail ?? '').trim().toLowerCase(),
    }, { emitEvent: false });
  }

  private noOnlyWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '');
    return !v.trim() ? { whitespace: true } : null;
  }

  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
  const v = String(control.value ?? '');
  if (!v) return null; 

  const errors: ValidationErrors = {};
  if (!/[A-Z]/.test(v)) errors['missingUpper'] = true;
  if (!/[a-z]/.test(v)) errors['missingLower'] = true;
  if (!/\d/.test(v)) errors['missingNumber'] = true;
  if (!/[!@#$%^&*(),.?":{}|<>_\-+=/\\[\]~`;']/.test(v)) errors['missingSpecial'] = true;

  return Object.keys(errors).length ? errors : null;
}

}